import { useState } from "react";
import { useTranslation } from "react-i18next";
import { App, Button, Spin, Table, Tooltip } from "antd";
import { observer } from "mobx-react-lite";

import {
  DISABLED_WEBGIS_STATES,
  NONEXISTANT_WEBGIS_STATES,
} from "../../api/team";
import { teamStore } from "../../stores/teamStore";
import { usePendingAction } from "../../utils/usePendingAction";

import type { MemberTeam, TeamRequest } from "../../api/team";

const formatWebGISUrl = (url: string) => url.replace(/^https:\/\//, "");
const getWebGISAuthUrl = (url: string) =>
  `${url.replace(/\/$/, "")}/oauth?na=next`;

export const MemberTeamsTab = observer(function MemberTeamsTab() {
  const { t } = useTranslation();
  const { modal, notification } = App.useApp();
  const { memberTeams: teams, memberTeamsLoading: loading } = teamStore;
  const invitations = teamStore.incomingInvitations;
  const [processingInvitation, setProcessingInvitation] = useState<
    string | null
  >(null);
  const [leavingTeam, setLeavingTeam] = useState<string | null>(null);
  const { run: runInvitationAction } = usePendingAction();
  const { run: runLeaveTeam } = usePendingAction();

  const renderWebGIS = (
    webgis?: string | null,
    webgisState?: string | null,
  ) => {
    if (
      webgis &&
      (webgisState === undefined ||
        (webgisState !== null &&
          !NONEXISTANT_WEBGIS_STATES.includes(webgisState)))
    ) {
      if (webgisState && DISABLED_WEBGIS_STATES.includes(webgisState)) {
        return (
          <Tooltip title={t("webgis.isBlockedStatus")}>
            <span className="team-manage-page__webgis-disabled">
              {formatWebGISUrl(webgis)}
            </span>
          </Tooltip>
        );
      }
      return (
        <a
          href={getWebGISAuthUrl(webgis)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {formatWebGISUrl(webgis)}
        </a>
      );
    }
    return (
      <span className="team-manage-page__no-webgis">{t("team.noWebGIS")}</span>
    );
  };

  const handleLeaveTeam = (team: MemberTeam) => {
    const owner = team.owner_username ?? team.owner_id;
    modal.confirm({
      className: "team-manage-page__cancel-invitation-modal",
      title: t("team.leaveTeamConfirm", { owner }),
      okText: t("team.leaveTeam"),
      cancelText: t("app.goBack"),
      okButtonProps: { danger: true },
      onOk: () =>
        runLeaveTeam(async () => {
          setLeavingTeam(team.owner_id);
          try {
            await teamStore.leaveMemberTeam(team.owner_id);
          } catch (error) {
            notification.error({ title: t("errors.shared.common_error") });
            throw error;
          } finally {
            setLeavingTeam(null);
          }
        }),
    });
  };

  const handleAccept = async (guid: string) => {
    await runInvitationAction(async () => {
      setProcessingInvitation(guid);
      try {
        await teamStore.acceptIncomingInvitation(guid);
      } catch {
        notification.error({ title: t("errors.shared.common_error") });
      } finally {
        setProcessingInvitation(null);
      }
    });
  };

  const handleDecline = (invitation: TeamRequest) => {
    modal.confirm({
      className: "team-manage-page__cancel-invitation-modal",
      title: t("team.declineInvitationConfirm", {
        email: invitation.email,
      }),
      okText: t("team.decline"),
      cancelText: t("app.goBack"),
      okButtonProps: { danger: true },
      onOk: () =>
        runInvitationAction(async () => {
          setProcessingInvitation(invitation.guid);
          try {
            await teamStore.declineIncomingInvitation(invitation.guid);
          } catch (error) {
            notification.error({ title: t("errors.shared.common_error") });
            throw error;
          } finally {
            setProcessingInvitation(null);
          }
        }),
    });
  };

  if (loading || teamStore.incomingInvitationsLoading) {
    return (
      <div className="team-manage-page__center">
        <Spin size="large" />
      </div>
    );
  }

  if (
    !loading &&
    !teamStore.incomingInvitationsLoading &&
    teams.length === 0 &&
    invitations.length === 0
  ) {
    return (
      <div className="team-manage-page__not-member">{t("team.notAMember")}</div>
    );
  }

  return (
    <div className="team-manage-page__member-teams">
      {teams.length > 0 && (
        <section className="team-manage-page__memberships">
          <Table<MemberTeam>
            className="team-manage-page__table"
            dataSource={teams}
            rowKey="owner_id"
            pagination={teams.length > 10 ? undefined : false}
            columns={[
              {
                title: t("team.teamColumn"),
                key: "team",
                render: (_: unknown, record: MemberTeam) => (
                  <div className="team-manage-page__invitee">
                    <span>
                      {t("team.teamOf", {
                        owner: record.owner_username ?? record.owner_id,
                      })}
                    </span>
                    {record.owner_email && (
                      <span className="team-manage-page__invitee-email">
                        {record.owner_email}
                      </span>
                    )}
                    <span className="team-manage-page__mobile-detail">
                      {renderWebGIS(record.webgis, record.webgis_state)}
                    </span>
                  </div>
                ),
              },
              {
                title: t("team.webgisColumn"),
                key: "webgis",
                className: "team-manage-page__desktop-column",
                render: (_: unknown, record: MemberTeam) =>
                  renderWebGIS(record.webgis, record.webgis_state),
              },
              {
                key: "action",
                align: "right" as const,
                className: "team-manage-page__action-column",
                render: (_: unknown, team: MemberTeam) => (
                  <Button
                    loading={leavingTeam === team.owner_id}
                    disabled={
                      leavingTeam !== null && leavingTeam !== team.owner_id
                    }
                    onClick={() => handleLeaveTeam(team)}
                  >
                    {t("team.leaveTeam")}
                  </Button>
                ),
              },
            ]}
          />
        </section>
      )}

      {(teamStore.incomingInvitationsLoading || invitations.length > 0) && (
        <section className="team-manage-page__pending-invitations">
          <h3 className="team-manage-page__pending-invitations-title">
            {t("team.pendingInvitations")}
          </h3>
          <Table<TeamRequest>
            className="team-manage-page__table"
            dataSource={invitations}
            rowKey="guid"
            loading={teamStore.incomingInvitationsLoading}
            pagination={invitations.length > 10 ? undefined : false}
            columns={[
              {
                title: t("team.teamColumn"),
                key: "team",
                render: (_: unknown, invitation: TeamRequest) => (
                  <div className="team-manage-page__invitee">
                    <span>
                      {t("team.teamOf", {
                        owner: invitation.username ?? invitation.email,
                      })}
                    </span>
                    {invitation.username && (
                      <span className="team-manage-page__mobile-detail">
                        {invitation.email}
                      </span>
                    )}
                    <span className="team-manage-page__mobile-detail">
                      {renderWebGIS(invitation.webgis)}
                    </span>
                  </div>
                ),
              },
              {
                title: t("team.webgisColumn"),
                key: "webgis",
                className: "team-manage-page__desktop-column",
                render: (_: unknown, invitation: TeamRequest) =>
                  invitation.webgis ? (
                    <a
                      href={getWebGISAuthUrl(invitation.webgis)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formatWebGISUrl(invitation.webgis)}
                    </a>
                  ) : (
                    <span className="team-manage-page__invitation-unavailable">
                      —
                    </span>
                  ),
              },
              {
                key: "action",
                align: "right" as const,
                className: "team-manage-page__action-column",
                render: (_: unknown, invitation: TeamRequest) => (
                  <div className="team-manage-page__invitation-actions">
                    <Button
                      type="primary"
                      loading={processingInvitation === invitation.guid}
                      disabled={
                        processingInvitation !== null &&
                        processingInvitation !== invitation.guid
                      }
                      onClick={() => handleAccept(invitation.guid)}
                    >
                      {t("team.accept")}
                    </Button>
                    <Button
                      danger
                      disabled={processingInvitation !== null}
                      onClick={() => handleDecline(invitation)}
                    >
                      {t("team.decline")}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </section>
      )}
    </div>
  );
});
